/**
 * AudioEngine
 *
 * Top-level facade that ties together AudioContextManager, AudioMixer,
 * SpatialAudioEngine, clip cache, and voice management.
 *
 * This is the primary entry point for game code to play audio.
 *
 * @module @engine/audio/AudioEngine
 */

import type {
  AudioPlaybackOptions,
  AudioFadeOptions,
  AudioEngineStats,
  AudioMixerDefinition,
  AudioContextConfig,
  AudioListenerState,
} from './types';
import { AudioContextManager, type AudioContextManagerOptions } from './AudioContextManager';
import { AudioMixer } from './AudioMixer';
import { AudioClip } from './AudioClip';
import { AudioSourceInstance } from './AudioSourceInstance';
import { SpatialAudioEngine } from './SpatialAudioEngine';

/**
 * Main audio engine facade.
 *
 * Manages clip loading/caching, playback instances (voices),
 * bus routing through the mixer, spatial listener, and voice stealing.
 */
export class AudioEngine {
  private _ctxManager: AudioContextManager;
  private _mixer: AudioMixer | null = null;
  private _spatial: SpatialAudioEngine | null = null;
  private _clips: Map<string, AudioClip> = new Map();
  private _voices: Map<number, AudioSourceInstance> = new Map();
  private _maxVoices: number;
  private _initialized: boolean = false;

  constructor(options: AudioContextManagerOptions = {}) {
    this._ctxManager = new AudioContextManager(options);
    this._maxVoices = options.maxVoices ?? 64;
  }

  // ==========================================================================
  // GETTERS
  // ==========================================================================

  get initialized(): boolean { return this._initialized; }
  get contextManager(): AudioContextManager { return this._ctxManager; }
  get mixer(): AudioMixer | null { return this._mixer; }
  get spatial(): SpatialAudioEngine | null { return this._spatial; }
  get context(): AudioContext { return this._ctxManager.context; }

  /** Number of currently active voices */
  get activeVoiceCount(): number {
    let count = 0;
    for (const v of this._voices.values()) {
      if (v.playing) count++;
    }
    return count;
  }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  /**
   * Initialize the audio engine. Must be called once.
   */
  async initialize(mixerDef?: AudioMixerDefinition): Promise<void> {
    if (this._initialized) return;

    await this._ctxManager.initialize();
    const ctx = this._ctxManager.context;

    // Create mixer
    this._mixer = new AudioMixer(ctx, this._ctxManager.masterGain);
    if (mixerDef) {
      this._mixer.loadDefinition(mixerDef);
    } else {
      this._mixer.loadDefaults();
    }

    // Create spatial engine
    this._spatial = new SpatialAudioEngine(ctx);

    this._initialized = true;
  }

  /**
   * Shutdown the audio engine.
   */
  async shutdown(): Promise<void> {
    this.stopAll();
    this._clips.forEach(clip => clip.unload());
    this._clips.clear();
    this._mixer?.destroy();
    this._mixer = null;
    this._spatial = null;
    await this._ctxManager.close();
    this._initialized = false;
  }

  /**
   * Resume audio context (needed after user gesture).
   */
  async resume(): Promise<void> {
    await this._ctxManager.resume();
  }

  /**
   * Suspend audio context.
   */
  async suspend(): Promise<void> {
    await this._ctxManager.suspend();
  }

  // ==========================================================================
  // CLIP MANAGEMENT
  // ==========================================================================

  /**
   * Load an audio clip from raw data.
   */
  async loadClip(guid: string, data: ArrayBuffer): Promise<AudioClip> {
    let clip = this._clips.get(guid);
    if (clip && clip.state === 'loaded') return clip;

    if (!clip) {
      clip = new AudioClip(guid);
      this._clips.set(guid, clip);
    }

    await clip.load(data, this._ctxManager.context);
    return clip;
  }

  /**
   * Get a loaded clip.
   */
  getClip(guid: string): AudioClip | undefined {
    return this._clips.get(guid);
  }

  /**
   * Unload a clip and stop all voices using it.
   */
  unloadClip(guid: string): void {
    // Stop any voices using this clip
    for (const [id, voice] of this._voices) {
      if (voice.clipGuid === guid) {
        voice.destroy();
        this._voices.delete(id);
      }
    }
    const clip = this._clips.get(guid);
    clip?.unload();
    this._clips.delete(guid);
  }

  /**
   * Check if a clip is loaded.
   */
  isClipLoaded(guid: string): boolean {
    const clip = this._clips.get(guid);
    return clip?.state === 'loaded';
  }

  // ==========================================================================
  // PLAYBACK
  // ==========================================================================

  /**
   * Play a sound. Returns a voice instance for controlling playback.
   *
   * @param clipGuid GUID of a loaded audio clip
   * @param options Playback options (volume, pitch, loop, bus, spatial, etc.)
   * @returns AudioSourceInstance or null if clip not loaded or voices exhausted
   */
  play(clipGuid: string, options: AudioPlaybackOptions = {}): AudioSourceInstance | null {
    const clip = this._clips.get(clipGuid);
    if (!clip || clip.state !== 'loaded' || !clip.buffer) return null;
    if (!this._mixer) return null;

    // Voice stealing: if at max voices, stop lowest priority
    if (this._voices.size >= this._maxVoices) {
      this._stealVoice(options.priority ?? 128);
    }

    // Get bus input
    const busId = options.busId ?? 'master';
    const busInput = this._mixer.getBusInput(busId) ?? this._mixer.getBusInput('master');
    if (!busInput) return null;

    // Create voice
    const voice = new AudioSourceInstance(
      this._ctxManager.context,
      clipGuid,
      busInput,
      options,
    );

    // Handle fade-in
    if (options.fadeIn && options.fadeIn > 0) {
      voice.volume = 0;
      voice.play(clip.buffer, options.delay ?? 0);
      voice.fade({
        duration: options.fadeIn,
        targetVolume: options.volume ?? 1,
      });
    } else {
      voice.play(clip.buffer, options.delay ?? 0);
    }

    // Track voice
    this._voices.set(voice.id, voice);
    voice.onEnded = () => {
      this._voices.delete(voice.id);
    };

    return voice;
  }

  /**
   * Play a one-shot sound (fire and forget, no return handle).
   */
  playOneShot(clipGuid: string, options: AudioPlaybackOptions = {}): void {
    const voice = this.play(clipGuid, { ...options, loop: false });
    // Voice will auto-cleanup via onEnded
    if (voice) {
      voice.onEnded = () => {
        voice.destroy();
        this._voices.delete(voice.id);
      };
    }
  }

  /**
   * Stop a specific voice.
   */
  stopVoice(voiceId: number): void {
    const voice = this._voices.get(voiceId);
    if (voice) {
      voice.destroy();
      this._voices.delete(voiceId);
    }
  }

  /**
   * Stop all voices.
   */
  stopAll(): void {
    for (const voice of this._voices.values()) {
      voice.destroy();
    }
    this._voices.clear();
  }

  /**
   * Stop all voices playing a specific clip.
   */
  stopClip(clipGuid: string): void {
    for (const [id, voice] of this._voices) {
      if (voice.clipGuid === clipGuid) {
        voice.destroy();
        this._voices.delete(id);
      }
    }
  }

  /**
   * Fade out all voices playing a clip.
   */
  fadeOutClip(clipGuid: string, duration: number = 0.5): void {
    for (const voice of this._voices.values()) {
      if (voice.clipGuid === clipGuid && voice.playing) {
        voice.fade({ duration, targetVolume: 0, stopAfterFade: true });
      }
    }
  }

  /**
   * Get all active voice instances.
   */
  getActiveVoices(): AudioSourceInstance[] {
    return Array.from(this._voices.values()).filter(v => v.playing);
  }

  /**
   * Get a voice instance by ID.
   */
  getVoice(voiceId: number): AudioSourceInstance | undefined {
    return this._voices.get(voiceId);
  }

  /**
   * Set volume of a specific voice.
   */
  setVoiceVolume(voiceId: number, volume: number): void {
    const voice = this._voices.get(voiceId);
    if (voice) voice.volume = volume;
  }

  /**
   * Get volume of a specific voice.
   */
  getVoiceVolume(voiceId: number): number {
    return this._voices.get(voiceId)?.volume ?? 0;
  }

  /**
   * Set pitch of a specific voice.
   */
  setVoicePitch(voiceId: number, pitch: number): void {
    const voice = this._voices.get(voiceId);
    if (voice) voice.pitch = pitch;
  }

  /**
   * Get pitch of a specific voice.
   */
  getVoicePitch(voiceId: number): number {
    return this._voices.get(voiceId)?.pitch ?? 1;
  }

  /**
   * Pause a specific voice.
   */
  pauseVoice(voiceId: number): void {
    const voice = this._voices.get(voiceId);
    if (voice) voice.pause();
  }

  /**
   * Resume a paused voice.
   */
  resumeVoice(voiceId: number): void {
    const voice = this._voices.get(voiceId);
    if (!voice || !voice.paused) return;
    const clip = this._clips.get(voice.clipGuid);
    if (clip?.buffer) {
      voice.resume(clip.buffer);
    }
  }

  /**
   * Check if a voice is currently playing.
   */
  isVoicePlaying(voiceId: number): boolean {
    return this._voices.get(voiceId)?.playing ?? false;
  }

  /**
   * Check if a voice is paused.
   */
  isVoicePaused(voiceId: number): boolean {
    return this._voices.get(voiceId)?.paused ?? false;
  }

  /**
   * Get the current playback time of a voice.
   */
  getVoiceTime(voiceId: number): number {
    return this._voices.get(voiceId)?.currentTime ?? 0;
  }

  /**
   * Set spatial position of a voice.
   */
  setVoicePosition(voiceId: number, x: number, y: number, z: number = 0): void {
    const voice = this._voices.get(voiceId);
    if (voice) voice.setPosition(x, y, z);
  }

  // ==========================================================================
  // LISTENER
  // ==========================================================================

  /**
   * Update the listener state (from AudioListener component).
   */
  setListenerState(state: AudioListenerState): void {
    this._spatial?.setListenerState(state);
  }

  /**
   * Update listener from 2D transform with orientation params.
   */
  updateListenerFrom2D(
    x: number, y: number, rotation: number,
    invertX?: boolean, invertY?: boolean,
    rotationOffset?: number, followRotation?: boolean,
  ): void {
    this._spatial?.updateFrom2DTransform(
      x, y, rotation,
      invertX, invertY, rotationOffset, followRotation,
    );
  }

  /**
   * Set global doppler configuration from the AudioListener component.
   *
   * @param dopplerFactor Global doppler intensity (0 = disabled, 1 = realistic)
   * @param speedOfSound Speed of sound in game units per second (343 ≈ real-world air)
   */
  setDopplerConfig(dopplerFactor: number, speedOfSound: number): void {
    if (!this._spatial) return;
    this._spatial.dopplerFactor = dopplerFactor;
    this._spatial.speedOfSound = speedOfSound;
  }

  /**
   * Update doppler pitch shifts for all spatial voices.
   * Should be called once per frame after listener & source positions are updated.
   */
  updateDoppler(deltaTime: number): void {
    if (!this._spatial) return;

    // Compute listener velocity from position delta
    this._spatial.updateVelocity(deltaTime);

    // Skip if doppler is globally disabled
    if (this._spatial.dopplerFactor === 0) {
      // Reset any lingering doppler shifts
      for (const voice of this._voices.values()) {
        if (voice.dopplerPitch !== 1) voice.dopplerPitch = 1;
      }
      return;
    }

    for (const voice of this._voices.values()) {
      if (!voice.playing || !voice.isSpatial) continue;

      const dopplerPitch = this._spatial.computeDopplerPitch(
        voice.positionX, voice.positionY, voice.positionZ,
        voice.velocityX, voice.velocityY, voice.velocityZ,
        voice.spatialDopplerFactor,
      );

      voice.dopplerPitch = dopplerPitch;
    }
  }

  // ==========================================================================
  // MIXER  
  // ==========================================================================

  /**
   * Load a mixer definition.
   */
  loadMixerDefinition(def: AudioMixerDefinition): void {
    if (!this._mixer) return;
    this._mixer.loadDefinition(def);
  }

  /**
   * Set bus volume.
   */
  setBusVolume(busId: string, volumeDb: number): void {
    const bus = this._mixer?.getBus(busId);
    if (bus) bus.volume = volumeDb;
  }

  /**
   * Smoothly transition to a mixer snapshot.
   *
   * @param snapshotId   ID of the snapshot to transition to
   * @param duration     Transition duration in seconds (if omitted, uses the snapshot's transitionTime)
   */
  transitionToSnapshot(snapshotId: string, duration?: number): void {
    this._mixer?.transitionToSnapshot(snapshotId, duration);
  }

  /**
   * Apply a mixer snapshot instantly (no transition).
   */
  applySnapshot(snapshotId: string): void {
    this._mixer?.applySnapshot(snapshotId);
  }

  /**
   * Set bus mute.
   */
  setBusMute(busId: string, muted: boolean): void {
    const bus = this._mixer?.getBus(busId);
    if (bus) bus.muted = muted;
  }

  /**
   * Set the master (listener) volume. Applied to the master gain node.
   */
  setMasterVolume(volume: number): void {
    this._ctxManager.masterGain.gain.setTargetAtTime(
      Math.max(0, volume),
      this._ctxManager.context.currentTime,
      0.02,
    );
  }

  // ==========================================================================
  // CONTEXT CONFIG
  // ==========================================================================

  /**
   * Change sample rate.
   */
  async setSampleRate(rate: AudioContextConfig['sampleRate']): Promise<void> {
    await this._ctxManager.setSampleRate(rate);
  }

  /**
   * Set output device.
   */
  async setOutputDevice(sinkId: string): Promise<void> {
    await this._ctxManager.setOutputDevice(sinkId);
  }

  /**
   * Get available output devices.
   */
  async getOutputDevices(): Promise<MediaDeviceInfo[]> {
    return this._ctxManager.getOutputDevices();
  }

  /**
   * Attach the single HTML audio element.
   */
  attachAudioElement(element: HTMLAudioElement): void {
    this._ctxManager.attachAudioElement(element);
  }

  // ==========================================================================
  // STATS
  // ==========================================================================

  /**
   * Get engine statistics.
   */
  getStats(): AudioEngineStats {
    return {
      contextState: this._ctxManager.state,
      sampleRate: this._ctxManager.sampleRate,
      currentTime: this._ctxManager.currentTime,
      baseLatency: this._ctxManager.baseLatency,
      outputLatency: this._ctxManager.outputLatency,
      activeVoices: this.activeVoiceCount,
      maxVoices: this._maxVoices,
      loadedClips: this._clips.size,
      activeBuses: this._mixer?.busIds.length ?? 0,
      channelCount: this._ctxManager.channelCount,
    };
  }

  /**
   * Update metering (call from render/tick loop).
   */
  updateMetering(): void {
    this._mixer?.updateMetering();
  }

  // ==========================================================================
  // PRIVATE
  // ==========================================================================

  /**
   * Steal the lowest-priority voice to make room for a new one.
   */
  private _stealVoice(newPriority: number): void {
    let lowestPriority = -1;
    let lowestVoiceId = -1;

    for (const [id, voice] of this._voices) {
      if (!voice.playing) {
        // Prefer to remove non-playing voices first
        voice.destroy();
        this._voices.delete(id);
        return;
      }
      // Higher priority number = less important
      if (voice.priority > lowestPriority) {
        lowestPriority = voice.priority;
        lowestVoiceId = id;
      }
    }

    // Only steal if new voice is more important (lower priority number)
    if (lowestVoiceId >= 0 && newPriority < lowestPriority) {
      const voice = this._voices.get(lowestVoiceId);
      voice?.destroy();
      this._voices.delete(lowestVoiceId);
    }
  }
}
